import { useState } from 'react'

import InfiniteScroll from 'react-infinite-scroller'

import {
  FlaggedType,
  MassActionRequest,
  ModerationItem,
  ModerationStatus,
  Post as PostType,
} from '@tribeplatform/gql-client/types'
import { Post } from '@tribeplatform/react-components/Post'
import { usePostModerationItems } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Card } from '@tribeplatform/react-ui-kit/Card'

import { MassActionBanner } from '../../Admin/MassActions/MassActionBanner.js'
import { EmptyState } from '../EmptyState.js'
import { ModerationItemNote } from '../ModerationItemNote.js'
import {
  ReportCategoryDropdown,
  ReportCategoryItem,
} from '../ReportCategoryDropdown.js'

import { PendingPostButtons } from './PendingPostButtons.js'
import { PendingPostsLoading } from './PendingPostsLoading.js'

export const PendingPosts = ({
  spaceId = undefined,
  flaggedBy = FlaggedType.SYSTEM,
}: {
  spaceId?: string
  flaggedBy?: FlaggedType
}) => {
  const [massActionRequest, setMassActionRequest] =
    useState<MassActionRequest>(null)
  const [selectedReportCategory, setSelectedReportCategory] =
    useState<ReportCategoryItem | null>(null)

  const {
    data,
    fetchNextPage = false,
    hasNextPage,
    isLoading,
  } = usePostModerationItems({
    fields: {
      pageInfo: 'basic',
      nodes: {
        entity: {
          onPost: {
            fields: 'all',
            owner: {
              member: {
                profilePicture: 'all',
              },
            },
            repliedTos: 'basic',
            space: {
              image: 'all',
            },
            tags: 'basic',
            authMemberProps: {
              permissions: 'all',
            },
          },
        },
        reporters:
          flaggedBy === FlaggedType.MEMBER
            ? {
                variables: { limit: 5 },
                fields: {
                  pageInfo: 'basic',
                  nodes: {
                    reporter: 'basic',
                  },
                },
              }
            : undefined,
      },
    },
    variables: {
      limit: 20,
      spaceId,
      status: ModerationStatus.REVIEW,
      reportCategories:
        flaggedBy === FlaggedType.MEMBER && !!selectedReportCategory
          ? [selectedReportCategory.value]
          : undefined,
      flaggedBy,
    },
  })

  const { nodes: moderationItems } =
    simplifyPaginatedResult<ModerationItem>(data)

  const shouldShowEmptyState =
    !isLoading && !hasNextPage && moderationItems?.length === 0

  return (
    <>
      {massActionRequest && (
        <div className="mb-5">
          <MassActionBanner massActionRequest={massActionRequest} />
        </div>
      )}
      {flaggedBy === FlaggedType.MEMBER && (
        <ReportCategoryDropdown
          className="mb-2"
          value={selectedReportCategory}
          onChange={setSelectedReportCategory}
        />
      )}
      {shouldShowEmptyState && <EmptyState />}
      {!shouldShowEmptyState && (
        <InfiniteScroll
          pageStart={0}
          loadMore={fetchNextPage}
          hasMore={hasNextPage || isLoading}
          threshold={800}
          className="flex flex-col space-y-5"
          loader={<PendingPostsLoading />}
        >
          {moderationItems?.map(moderationItem => (
            <Card key={moderationItem?.id}>
              <Card.Content className="flex flex-col space-y-4">
                <ModerationItemNote
                  flaggedBy={flaggedBy}
                  moderationItem={moderationItem}
                />
                <Post
                  post={moderationItem?.entity as PostType}
                  context="moderation"
                  replyBar={false}
                />
                <PendingPostButtons
                  moderationItem={moderationItem}
                  setMassActonRequest={setMassActionRequest}
                />
              </Card.Content>
            </Card>
          ))}
        </InfiniteScroll>
      )}
    </>
  )
}
